import React, { useRef } from 'react';
import { Link, type Href } from 'expo-router';
import { StyleSheet, View } from 'react-native';
import Animated, { useAnimatedStyle, useDerivedValue, withTiming } from 'react-native-reanimated';

import {
  ContentfulImageTransformOptions,
  DisplayText,
  FallbackImage,
  Icon,
  theme,
  usePressableState,
  useResponsive,
  webOnlyStyles,
} from '@sg/garnish';

const HomeFooterFallbackImage = require('./home-footer-fallback.png');

// ─────────────────────────────────────────────────────────────────────────────

export const HomeFooter = (props: HomeFooterProps) => {
  const { title, imageUrl, imageA11y, menuHref, trackMenuView } = props;

  const { match } = useResponsive();
  const minHeight = match([styles.minHeightXS, styles.minHeightSM]);
  const navMargin = match([styles.bottomNavigationMargin, null]);

  const { ref, animatedStyle } = useInteractionAnimation();

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <Link
      ref={ref}
      href={menuHref}
      style={[styles.container, minHeight, navMargin, pointerCursorStyle]}
      onPress={trackMenuView}
    >
      <View style={styles.content}>
        <Animated.View style={[styles.image, minHeight, animatedStyle]}>
          <FallbackImage
            aria-label={imageA11y}
            baseUrl={imageUrl}
            defaultImage={HomeFooterFallbackImage}
            style={styles.image}
            contentfulOptions={match([CONTENTFUL_OPTIONS_XS, CONTENTFUL_OPTIONS_SM])}
          />
        </Animated.View>

        <DisplayText bold sizeMatch={['32', '48']} style={styles.title}>
          {title}
        </DisplayText>

        <View style={styles.link}>
          <Icon name="IconArrowRight" width={24} height={24} />
        </View>
      </View>
    </Link>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const useInteractionAnimation = () => {
  const ref = useRef(null);
  const { isInteracting } = usePressableState(ref);

  const scale = useDerivedValue(
    () => withTiming(isInteracting ? 1.05 : 1, { duration: ANIMATION_DURATION }),
    [isInteracting],
  );

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
  }));

  return { ref, animatedStyle };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const ANIMATION_DURATION = theme.transitions.base;
const IMAGE_HEIGHT_XS = 110;
const IMAGE_HEIGHT_SM = 330;

const SHARED_CONTENTFUL_OPTIONS: Partial<ContentfulImageTransformOptions> = {
  fm: 'webp',
  fit: 'fill',
  q: 70,
};

const CONTENTFUL_OPTIONS_XS: Partial<ContentfulImageTransformOptions> = {
  ...SHARED_CONTENTFUL_OPTIONS,
  h: IMAGE_HEIGHT_XS,
};

const CONTENTFUL_OPTIONS_SM: Partial<ContentfulImageTransformOptions> = {
  ...SHARED_CONTENTFUL_OPTIONS,
  h: IMAGE_HEIGHT_SM,
};

const pointerCursorStyle = webOnlyStyles({ cursor: 'pointer' });

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    marginTop: 'auto',
    overflow: 'hidden',
  },
  content: {
    flexDirection: 'row',
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing['6'],
  },
  bottomNavigationMargin: {
    marginBottom: theme.spacing['25'],
  },
  minHeightXS: {
    minHeight: IMAGE_HEIGHT_XS,
  },
  minHeightSM: {
    minHeight: IMAGE_HEIGHT_SM,
  },
  image: {
    ...StyleSheet.absoluteFill,
    backgroundColor: theme.colors.DARK_KALE,
    width: '100%',
  },
  title: {
    color: theme.colors.WHITE,
  },
  link: {
    width: 48,
    height: 48,
    borderRadius: 48,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.WHITE,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type HomeFooterProps = {
  title: string;
  imageUrl?: string;
  imageA11y: string;
  menuHref: Href;
  trackMenuView: () => void;
};
